import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, AfterViewInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { AuthService } from '../../../services/auth.service';
import { GenericInputComponent } from '../../../components/generic-input/generic-input.component';
import { ThemeService } from '../../../services/theme.service';
import { CountdownComponent } from "../../../components/double-authentication-countdown/double-authentication-countdown.component";
import { OpenModalConfirmService } from '../../../services/open-modal-confirm.service';
@Component({
  selector: 'app-irancho-login',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatSnackBarModule,
    GenericInputComponent,
    TranslateModule,
    CountdownComponent
],
  templateUrl: './irancho-login.component.html',
  styleUrl: './irancho-login.component.scss'
})
export class IranchoLoginComponent implements OnInit, AfterViewInit {
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private _snackBar = inject(MatSnackBar);
  private router = inject(Router);
  private translate = inject(TranslateService);
  private themeService = inject(ThemeService);
  private openModalConfirmService = inject(OpenModalConfirmService);

  public showLogin = true;
  public showChangePassword = false;
  public showPassword = false;
  public showForgotPassword = false;
  public showConfirmingEmail = false;
  doubleAuthentication = false;
  timeExpired = false;
  doubleAuthenticationCount = 3;

  doubleAuthenticationForm: FormGroup = this.fb.group({
    code: ['', Validators.required],
  });

  form: FormGroup = this.fb.group({
    username: ['', Validators.required],
    currentPassword: ['', Validators.required],
  });

  changePasswordForm: FormGroup = this.fb.group({
    username: ['', Validators.required],
    oldPassword: ['', Validators.required],
    newPassword: ['', [Validators.required, Validators.minLength(8), this.passwordStrengthValidator]],
    confirmNewPassword: ['', Validators.required]
  });

  resetPasswordForm: FormGroup = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
  });

  ngOnInit(): void {
    sessionStorage.clear();
    localStorage.clear();
    this.themeService.setThemeByThemeName('IRANCHO');
    sessionStorage.setItem('systemType', 'IRANCHO');
  }

  ngAfterViewInit(): void {
    this.themeService.setThemeByThemeName('IRANCHO');
  }

  handleClickRequestQuote() {
    this.showLogin = false;
  }

  handleClickCloseRegister() {
    this.showLogin = true;
    this.showForgotPassword = false;
  }

  toggleShowPassword() {
    this.showPassword = !this.showPassword;
  }

  handleForgetPassword(): void {
    this.showForgotPassword = true;
    this.showLogin = false;
    this.showConfirmingEmail = true;
  }

  redirectByToken () {
    const userItem = sessionStorage.getItem('user');
    const user = userItem ? JSON.parse(userItem) : {};
    if (user.authorities.some((item: string) => item === 'ROLE_IRANCHO_ADMIN')) {
      this.themeService.setThemeByThemeName('IRANCHO');
      sessionStorage.setItem('systemType', 'IRANCHO');
      this.router.navigate(['/panel/certifications/pending-approval']);
      this._snackBar.open(this.translate.instant('LOGIN.SUCCESS'), undefined, {
        panelClass: ['alert_success'],
        horizontalPosition: "right",
        verticalPosition: "top"
      });
    } else if (user.confirmPersonalData) {
      this.router.navigate(['/public-panel/'] ,{
        queryParams: { userId: user.customerId},
      });
    } else if (user.authorities.some((item: string) => item === 'ROLE_IRANCHO_REQUESTER')) {
      this.themeService.setThemeByThemeName('IRANCHO');
      sessionStorage.setItem('systemType', 'IRANCHO');
      this.router.navigate(['/client-panel/']);
    } else {
      this._snackBar.open(this.translate.instant('LOGIN.ERROR'), undefined, {
        panelClass: ['alert_success'],
        horizontalPosition: "right",
        verticalPosition: "top"
      });
    }
  }

  submitForm() {
    if (this.form.valid) {
      const username = this.form.get('username')?.value;
      const password = this.form.get('currentPassword')?.value;
      this.authService.login(username, password).subscribe(
        res => {
          this.redirectByToken();
        },
        (error) => {
          const errorType = JSON.parse(error.error)
          if (errorType?.status === "TWO_FACTOR_REQUIRED") {
            if(this.timeExpired === true) {
              this.openModalConfirmService.openModalConfirm({
                text: 'Token expirado!.',
                subText: 'Um novo token de autenticação foi enviado para o e-mail do usuário.',
                type: 'warning',
                hideCancelButton: true,
              }).subscribe(() => {
                this.doubleAuthentication = true;
                this.showLogin = false;
                this.timeExpired = false;
                this.showChangePassword = false;
              });
              return;
            }
            this.openModalConfirmService.openModalConfirm({
              text: 'Um token de autenticação foi enviado para o e-mail do usuário.',
              subText: 'Digite o token recebido para prosseguir com o acesso ao sistema.',
              type: 'warning',
              hideCancelButton: true,
            }).subscribe(() => {
              this.doubleAuthentication = true;
              this.showLogin = false;
              this.showChangePassword = false;
            });
            return;
          }
          if (errorType?.status === 'CHANGE_PASSWORD') {
            this.showChangePassword = true;
            this.showLogin = false;
            this._snackBar.open(
              this.translate.instant('LOGIN.CHANGE_PASSWORD'),
              undefined,
              {
                panelClass: ['alert_error'],
                horizontalPosition: 'right',
                verticalPosition: 'top',
              }
            );
          } else {
            this._snackBar.open(
              this.translate.instant('LOGIN.ERROR'),
              undefined,
              {
                panelClass: ['alert_error'],
                horizontalPosition: 'right',
                verticalPosition: 'top',
              }
            );
          }
        }
      )
    }
  }

  submitChangePasswordForm() {
    this.changePasswordForm.markAllAsTouched();
    const oldPassword = this.form.get('currentPassword')?.value;
    const username = this.form.get('username')?.value;
    this.changePasswordForm.get('oldPassword')?.setValue(oldPassword);
    this.changePasswordForm.get('username')?.setValue(username);

    if (this.changePasswordForm.valid) {
      const payload = {
        username: this.changePasswordForm.get('username')?.value.trim(),
        oldPassword: this.changePasswordForm.get('oldPassword')?.value.trim(),
        newPassword: this.changePasswordForm.get('newPassword')?.value.trim(),
      }
      this.authService.changePassword(payload).subscribe({
        next: res => {
          this.form.get('currentPassword')?.setValue(this.changePasswordForm.get('newPassword')?.value.trim());
          this.submitForm();
        },
        error: error => {
          this._snackBar.open(this.translate.instant('CHANGE-PASSWORD.ERROR'), undefined, {
            panelClass: ['alert_error'],
            horizontalPosition: "right",
            verticalPosition: "top"
          });
        }
      });
    }
  }

  submitResetPasswordForm(): void {
    const frontUrl = window.location.href;

    this.resetPasswordForm.markAllAsTouched();
    if (this.resetPasswordForm.valid) {
      const email = this.resetPasswordForm.get('email')?.value.trim();
      this.authService.sendEmailResetPassword(email, frontUrl).subscribe({
        next: () => {
          this._snackBar.open(this.translate.instant('FORGOT-PASSWORD.SUCCESS'), undefined, {
            panelClass: ['alert_success'],
            horizontalPosition: "right",
            verticalPosition: "top"
          });
          this.handleClickCloseRegister();
        },
        error: error => {
          this._snackBar.open(this.translate.instant('FORGOT-PASSWORD.ERROR'), undefined, {
            panelClass: ['alert_error'],
            horizontalPosition: "right",
            verticalPosition: "top"
          });
        }
      });
    }
  }

  passwordStrengthValidator(control: AbstractControl): ValidationErrors | null {
    const value: string = control.value || '';

    const hasUpperCase = /[A-Z]/.test(value);
    const hasLowerCase = /[a-z]/.test(value);
    const hasNumber = /\d/.test(value);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);

    const isValid =
      value.length >= 8 &&
      hasUpperCase &&
      hasLowerCase &&
      hasNumber &&
      hasSpecialChar;

    return isValid ? null : { passwordStrength: true };
  }

  submitDoubleAuthentication() {
    const username = this.form.get('username')?.value;
    const code = this.doubleAuthenticationForm.get('code')?.value;
    this.doubleAuthenticationForm.markAllAsTouched();

    if (this.doubleAuthenticationForm.valid) {
      this.authService.doubleAuthentication(username, code).subscribe({
        next: () => {
          this._snackBar.open(
            this.translate.instant('FORGOT-PASSWORD.SUCCESS'),
            undefined,
            {
              panelClass: ['alert_success'],
              horizontalPosition: 'right',
              verticalPosition: 'top',
            }
          );
          this.redirectByToken();
        },
        error: (error) => {
          this.doubleAuthenticationCount = this.doubleAuthenticationCount - 1;
          this.doubleAuthenticationForm.get('code')?.setValue('');

          if (this.doubleAuthenticationCount > 0) {
            this.openModalConfirmService
              .openModalConfirm({
                text: 'Código inválido.',
                subText: `Você ainda possui ${
                  this.doubleAuthenticationCount
                } tentativa${
                  this.doubleAuthenticationCount > 1 ? 's' : ''
                } restante${this.doubleAuthenticationCount > 1 ? 's' : ''}.`,
                type: 'error',
                hideCancelButton: true,
              })
              .subscribe(() => {});
          } else {
            this.openModalConfirmService
              .openModalConfirm({
                text: 'Número máximo de tentativas atingido.',
                subText:
                  'Por favor, tente novamente mais tarde ou entre em contato com a equipe de suporte para obter assistência.',
                type: 'warning',
                hideCancelButton: true,
              })
              .subscribe(() => {
                this.doubleAuthentication = false;
                this.showLogin = true;
                this.doubleAuthenticationCount = 3;
              });
          }
        },
      });
    }
  }

  onCountdownFinished() {
    this.timeExpired = true;
    this.submitForm();
  }
}
